import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { PermissionService } from "./permission.service";

/**
 * Route guard that blocks navigation to a screen the caller's RBAC permission
 * set does not grant. Complements AuthGuard: AuthGuard answers "is someone
 * logged in?", this answers "may this user see this screen?".
 *
 * UX only, never an authorization boundary (dashboard-security skill, rule
 * #1): a denied route here just redirects; the API re-validates every read
 * and write regardless of what this guard decided.
 *
 * Usage: `canActivate: mapToCanActivate([AuthGuard, PermissionGuard])` with
 * `data: { permission: 'users.read' }` on the route. AuthGuard runs first —
 * order matters, an unauthenticated caller should hit the login redirect,
 * not a permission check. A route with no `data.permission` is allowed
 * through untouched (most routes; the full per-route mapping is a later
 * task).
 *
 * Timing + failure: PermissionService.load() resolves asynchronously after
 * login, so this guard must not read has()/has$() before the set has loaded
 * at least once — doing so would read an empty set as "denied" on a fresh
 * navigation or a refresh on a deep link. It drives off load() directly
 * (shared/deduped/cached — see permission.service.ts) rather than waiting on
 * ready$: load() resolves immediately with the current set if already
 * loaded, or waits for the in-flight/new request otherwise, so the guard
 * always gets a terminal notification. On a load() failure the guard does
 * NOT hang and does NOT throw — it redirects to /dashboard (fail-safe; the
 * API is the real authorization boundary regardless of what this guard
 * decides, so a redirect here costs UX, not security).
 */
@Injectable({ providedIn: "root" })
export class PermissionGuard implements CanActivate {
  public constructor(
    private readonly permissionService: PermissionService,
    private readonly router: Router
  ) {}

  public canActivate(next: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const permission: string | undefined = next.data["permission"] as string | undefined;
    if (!permission) {
      return of(true);
    }

    return this.permissionService.load().pipe(
      map((): boolean | UrlTree => this.decide(permission)),
      catchError((): Observable<UrlTree> => of(this.router.createUrlTree(["/dashboard"])))
    );
  }

  private decide(permission: string): boolean | UrlTree {
    return this.permissionService.has(permission) ? true : this.router.createUrlTree(["/dashboard"]);
  }
}
